// Upgrades BlockNote's default <video>/<audio> elements into proper iframe
// embeds when the URL belongs to a known provider (YouTube, Vimeo, SoundCloud,
// Spotify, Dailymotion, Twitch, etc.). Direct media URLs (.mp4, .mp3, …) are
// left untouched so the native HTML5 player keeps working.
//
// Designed to be safe in both editable mode (NoteEditor) and read-only mode
// (SharedNoteView) — it only mutates the rendered DOM, never the editor
// document, so persistence is unaffected.

export type EmbedKind = 'video' | 'audio';

const YOUTUBE_RELAY_URL = 'https://ios.mindnote.online/youtube-embed.html';

const stripHashAndQuery = (u: string) => u.split('#')[0].split('?')[0];

const decodeHtmlEntities = (value: string) => {
  if (!value || typeof document === 'undefined') return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const normalizeCandidateUrl = (value: string) => decodeHtmlEntities(value || '').trim();

const isNativeIOSWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const capacitor = (window as typeof window & {
    Capacitor?: {
      getPlatform?: () => string;
      isNativePlatform?: () => boolean;
    };
  }).Capacitor;
  if (capacitor?.getPlatform?.() === 'ios') return true;

  const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent || '') ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;

  if (capacitor?.isNativePlatform?.()) return true;

  const protocol = window.location.protocol.toLowerCase();
  return protocol !== 'http:' && protocol !== 'https:';
};

const buildYouTubeEmbedSrc = (id: string, startSeconds = 0) => {
  const params = new URLSearchParams({
    playsinline: '1',
    rel: '0',
  });
  if (startSeconds > 0) params.set('start', String(startSeconds));

  if (isNativeIOSWebView()) {
    const relayUrl = new URL(YOUTUBE_RELAY_URL);
    relayUrl.searchParams.set('v', id);
    params.forEach((value, key) => relayUrl.searchParams.set(key, value));
    return relayUrl.toString();
  }

  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
};

/**
 * Returns an iframe src + the media kind for any URL we know how to embed.
 * Returns null for direct media files / unknown URLs so the caller leaves
 * them alone (the native <video>/<audio> element stays in place).
 */
export const resolveEmbedUrl = (
  rawUrl: string,
): { src: string; kind: EmbedKind; aspect: 'video' | 'audio' } | null => {
  rawUrl = normalizeCandidateUrl(rawUrl);
  if (!rawUrl) return null;
  let url: URL;
  try {
    url = new URL(rawUrl, window.location.href);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '').toLowerCase();
  const path = url.pathname;

  // ---------- YouTube ----------
  // iOS WKWebView custom-scheme origins (capacitor://localhost / MindNote://localhost)
  // do not provide the HTTPS referrer YouTube now requires, causing Error 153.
  // In native iOS only, route through a tiny HTTPS relay page on our domain;
  // web/mobile-browser embeds continue to use the direct privacy-enhanced URL.
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtube-nocookie.com') {
    let id: string | null = null;
    if (path === '/watch') id = url.searchParams.get('v');
    else if (path.startsWith('/embed/')) id = path.slice('/embed/'.length).split('/')[0];
    else if (path.startsWith('/shorts/')) id = path.slice('/shorts/'.length).split('/')[0];
    else if (path.startsWith('/live/')) id = path.slice('/live/'.length).split('/')[0];
    if (id) {
      const t = url.searchParams.get('t') || url.searchParams.get('start');
      const startSeconds = t ? parseInt(String(t), 10) || 0 : 0;
      return { src: buildYouTubeEmbedSrc(id, startSeconds), kind: 'video', aspect: 'video' };
    }
  }
  if (host === 'youtu.be') {
    const id = path.slice(1).split('/')[0];
    if (id) {
      const t = url.searchParams.get('t');
      const startSeconds = t ? parseInt(String(t), 10) || 0 : 0;
      return { src: buildYouTubeEmbedSrc(id, startSeconds), kind: 'video', aspect: 'video' };
    }
  }


  // ---------- Vimeo ----------
  if (host === 'vimeo.com') {
    const id = path.slice(1).split('/')[0];
    if (/^\d+$/.test(id)) {
      return { src: `https://player.vimeo.com/video/${id}`, kind: 'video', aspect: 'video' };
    }
  }
  if (host === 'player.vimeo.com') {
    return { src: rawUrl, kind: 'video', aspect: 'video' };
  }

  // ---------- Dailymotion ----------
  if (host === 'dailymotion.com') {
    if (path.startsWith('/video/')) {
      const id = path.slice('/video/'.length).split('/')[0];
      if (id) return { src: `https://www.dailymotion.com/embed/video/${id}`, kind: 'video', aspect: 'video' };
    }
  }
  if (host === 'dai.ly') {
    const id = path.slice(1);
    if (id) return { src: `https://www.dailymotion.com/embed/video/${id}`, kind: 'video', aspect: 'video' };
  }

  // ---------- Twitch ----------
  if (host === 'twitch.tv' || host === 'clips.twitch.tv') {
    const parent = window.location.hostname || 'localhost';
    if (path.startsWith('/videos/')) {
      const id = path.slice('/videos/'.length).split('/')[0];
      return { src: `https://player.twitch.tv/?video=${id}&parent=${parent}`, kind: 'video', aspect: 'video' };
    }
    const channel = path.slice(1).split('/')[0];
    if (channel) {
      return { src: `https://player.twitch.tv/?channel=${channel}&parent=${parent}`, kind: 'video', aspect: 'video' };
    }
  }

  if (host === 'w.soundcloud.com' && path.startsWith('/player/')) {
    return { src: rawUrl, kind: 'audio', aspect: 'audio' };
  }

  // ---------- SoundCloud ----------
  if (host === 'soundcloud.com' || host === 'on.soundcloud.com' || host === 'm.soundcloud.com') {
    const cleaned = stripHashAndQuery(rawUrl);
    return {
      src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(cleaned)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`,
      kind: 'audio',
      aspect: 'audio',
    };
  }

  // ---------- Spotify ----------
  if (host === 'open.spotify.com' || host === 'spotify.com') {
    // /track/<id>, /episode/<id>, /show/<id>, /playlist/<id>, /album/<id>
    const m = path.match(/^\/(track|episode|show|playlist|album|artist)\/([A-Za-z0-9]+)/);
    if (m) {
      const isVideo = m[1] === 'show' || m[1] === 'episode';
      return {
        src: `https://open.spotify.com/embed/${m[1]}/${m[2]}`,
        kind: isVideo ? 'video' : 'audio',
        aspect: 'audio',
      };
    }
  }

  // ---------- Apple Podcasts / Music ----------
  if (host === 'podcasts.apple.com' || host === 'music.apple.com') {
    const embedHost = host.replace('apple.com', 'apple.com').replace('podcasts.', 'embed.podcasts.').replace('music.', 'embed.music.');
    return { src: `https://${embedHost}${path}${url.search}`, kind: 'audio', aspect: 'audio' };
  }

  // ---------- Loom ----------
  if (host === 'loom.com' || host === 'www.loom.com') {
    if (path.startsWith('/share/')) {
      const id = path.slice('/share/'.length).split('/')[0];
      return { src: `https://www.loom.com/embed/${id}`, kind: 'video', aspect: 'video' };
    }
  }

  // ---------- Wistia ----------
  if (host.endsWith('wistia.com') || host.endsWith('wistia.net')) {
    const m = path.match(/medias\/([A-Za-z0-9]+)/);
    if (m) return { src: `https://fast.wistia.net/embed/iframe/${m[1]}`, kind: 'video', aspect: 'video' };
  }

  return null;
};

const UPGRADED_ATTR = 'data-mn-embed-upgraded';

const makeMediaShellFullWidth = (element: HTMLElement) => {
  const fileWrapper = element.closest<HTMLElement>('.bn-file-block-content-wrapper');
  const visualWrapper = element.closest<HTMLElement>('.bn-visual-media-wrapper');
  [fileWrapper, visualWrapper].forEach((wrapper) => {
    if (!wrapper) return;
    wrapper.style.width = '100%';
    wrapper.style.maxWidth = '100%';
  });
};

const buildIframeWrapper = (embed: { src: string; aspect: 'video' | 'audio' }, originalUrl: string) => {
  const wrapper = document.createElement('div');
  wrapper.setAttribute(UPGRADED_ATTR, 'true');
  wrapper.setAttribute('data-mn-embed-src', originalUrl);
  wrapper.setAttribute('contenteditable', 'false');
  wrapper.setAttribute('draggable', 'false');
  wrapper.className = 'mn-embed-wrapper';
  if (embed.aspect === 'video') {
    wrapper.style.cssText = 'position:relative;width:100%;max-width:100%;padding-top:56.25%;border-radius:8px;overflow:hidden;background:#000;';
  } else {
    wrapper.style.cssText = 'width:100%;max-width:100%;border-radius:8px;overflow:hidden;';
  }

  const iframe = document.createElement('iframe');
  iframe.src = embed.src;
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
  iframe.setAttribute('allowfullscreen', 'true');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  if (embed.aspect === 'video') {
    iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';
  } else {
    iframe.style.cssText = 'width:100%;height:166px;border:0;display:block;';
  }
  wrapper.appendChild(iframe);
  return wrapper;
};

const getElementMediaUrl = (el: HTMLElement) => {
  const candidates = [
    el.getAttribute('src'),
    (el as HTMLMediaElement).currentSrc,
    (el as HTMLMediaElement).src,
    el.getAttribute('href'),
    el.closest<HTMLAnchorElement>('a[href]')?.getAttribute('href'),
    el.querySelector<HTMLSourceElement>('source[src]')?.getAttribute('src'),
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCandidateUrl(candidate || '');
    if (normalized) return normalized;
  }
  return '';
};

const findMediaReplacementTarget = (el: HTMLElement) => {
  const block = el.closest<HTMLElement>('[data-content-type="video"], [data-content-type="audio"]');
  const link = el.closest<HTMLAnchorElement>('a[href]');
  const fileWrapper = el.closest<HTMLElement>('.bn-file-block-content-wrapper');
  const visualWrapper = el.closest<HTMLElement>('.bn-visual-media-wrapper');
  const blockContent = el.closest<HTMLElement>('.bn-block-content');

  // In read-only/shared notes BlockNote can render a provider URL as a broken
  // native <video>/<audio> plus a clickable link wrapper. Replacing the whole
  // visual/file wrapper prevents taps from falling through to the external URL.
  return fileWrapper || visualWrapper || link || blockContent || block || el;
};

/**
 * Walks a container and replaces any <video>/<audio> whose src is a known
 * embed-provider URL with an iframe wrapper. Also handles links that
 * BlockNote may have rendered for unknown URLs.
 */
export const upgradeEmbedsIn = (root: HTMLElement) => {
  if (!root) return;

  const mediaEls = root.querySelectorAll<HTMLMediaElement>('video, audio');
  mediaEls.forEach((el) => {
    if (el.closest(`[${UPGRADED_ATTR}]`)) return;
    const src = getElementMediaUrl(el);
    const embed = resolveEmbedUrl(src);
    if (!embed) return;
    makeMediaShellFullWidth(el);
    const wrapper = buildIframeWrapper({ src: embed.src, aspect: embed.aspect }, src);
    findMediaReplacementTarget(el).replaceWith(wrapper);
  });

  // BlockNote falls back to an "Open in new tab" anchor when a video/audio
  // block has a non-direct URL — swap that out for the iframe too.
  const fileBlocks = root.querySelectorAll<HTMLElement>(
    '[data-content-type="video"], [data-content-type="audio"]',
  );
  fileBlocks.forEach((block) => {
    if (block.querySelector(`[${UPGRADED_ATTR}]`)) return;
    if (block.querySelector('video[src], audio[src]')) return;
    const anchor = block.querySelector<HTMLAnchorElement>('a[href]');
    const href = normalizeCandidateUrl(anchor?.getAttribute('href') || block.getAttribute('data-url') || '');
    if (!href) return;
    const embed = resolveEmbedUrl(href);
    if (!embed) return;
    makeMediaShellFullWidth(block);
    const wrapper = buildIframeWrapper({ src: embed.src, aspect: embed.aspect }, href);
    const target = anchor ? findMediaReplacementTarget(anchor) : block;
    target.replaceWith(wrapper);
  });

  root.querySelectorAll<HTMLAnchorElement>('.bn-block-content a[href]').forEach((anchor) => {
    if (anchor.closest(`[${UPGRADED_ATTR}]`)) return;
    const href = normalizeCandidateUrl(anchor.getAttribute('href') || '');
    const embed = resolveEmbedUrl(href);
    if (!embed) return;
    const wrapper = buildIframeWrapper({ src: embed.src, aspect: embed.aspect }, href);
    anchor.replaceWith(wrapper);
  });
};

/** Attach a MutationObserver so embeds stay upgraded across re-renders. */
export const attachEmbedUpgrader = (root: HTMLElement): (() => void) => {
  if (!root) return () => {};
  let scheduled = 0;
  const run = () => {
    scheduled = 0;
    upgradeEmbedsIn(root);
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = window.requestAnimationFrame(run);
  };

  schedule();
  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'href'] });
  return () => {
    observer.disconnect();
    if (scheduled) cancelAnimationFrame(scheduled);
  };
};
